import { useEffect, useState } from "react";
import type { NavigateFunction } from "react-router-dom";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  claimOneTimeToken,
  releaseOneTimeToken
} from "src/lib/oneTimeTokenGuard";
import { authenticationState } from "src/state/state";
import type { TranslationKey } from "src/types/translationKey";
import BlockingLoadingOverlayController from "src/ui/components/BlockingLoadingOverlay/BlockingLoadingOverlayController";
import reportErrorSentry from "./reportErrorSentry";

export const SAML_FUNNEL_KEY_SESSION_STORAGE_KEY = "samlFunnel";

interface ExchangeSamlTokenParams {
  token: string;
  funnel: string | null;
  navigate: NavigateFunction;
  onError: (error: TranslationKey) => void;
  onSettled: () => void;
}

/**
 * Runs the SAML handover: exchange the partner's single-use token for a session,
 * then leave for the app.
 *
 * Navigation happens as soon as the credentials are persisted rather than after
 * the post-login bootstrap, which has no timeout — waiting on it left members on
 * a blank page under the blocking overlay indefinitely.
 */
const exchangeSamlToken = ({
  token,
  funnel,
  navigate,
  onError,
  onSettled
}: ExchangeSamlTokenParams): void => {
  // Claim before prepareForExternalTokenLogin: a Back button or reload mounts a
  // fresh tree and would otherwise re-send a token the backend already consumed.
  if (!claimOneTimeToken("saml", token)) {
    // The usual way to get here is Back after a handover that worked, so send
    // the member on rather than showing them a login form they don't need.
    if (authenticationState.state.authenticationStatus === "authenticated") {
      navigate("/app?from=saml");
    } else {
      // The token stays in the URL, so a member whose session did not survive
      // lands here with a token that can no longer be sent. Say so above the
      // login form instead of silently rendering it with no explanation.
      onError("samltoken.error");
    }
    onSettled();
    return;
  }

  BlockingLoadingOverlayController.startLoading({
    bg: "branded"
  });

  // Prepare app for external SAML token login to avoid stale sessions
  authenticationState.prepareForExternalTokenLogin();

  if (funnel) {
    sessionStorage.setItem(SAML_FUNNEL_KEY_SESSION_STORAGE_KEY, funnel);
  }

  void authenticationState
    .authWithSamlToken(token)
    .then(() => {
      // Navigate to /app and let AppAccessCubit handle funnel redirects
      navigate("/app?from=saml");
    })
    .catch((e: unknown) => {
      // The exchange failed, so the token may never have reached the backend.
      // Let the member retry rather than burning it for the rest of the session.
      releaseOneTimeToken("saml", token);
      reportErrorSentry(e);
      onError("samltoken.error");
    })
    .finally(() => {
      BlockingLoadingOverlayController.endLoading();
      onSettled();
    });
};

const useSamlAuth = (): { loading: boolean; error: TranslationKey | null } => {
  const { pathname } = useLocation();
  const [query] = useSearchParams();
  const [error, setError] = useState<TranslationKey | null>(null);
  const token = query.get("token") ?? query.get("lookupKey");
  const funnel = query.get("funnel");
  const pathMatchLogin = pathname.startsWith("/auth/login");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (token && pathMatchLogin) {
      setLoading(true);
      exchangeSamlToken({
        token,
        funnel,
        navigate,
        onError: setError,
        onSettled: () => setLoading(false)
      });
    } else {
      setLoading(false);
    }
  }, [token, pathMatchLogin, funnel, navigate]);

  return {
    error,
    loading: Boolean(token && loading)
  };
};

export default useSamlAuth;
